import { z } from 'zod'
import {
  blockContentSchema,
  type Lang,
  type NodeContent,
  nodeContentSchema,
  runOfString,
  runsOf,
  withRunAt,
} from './content'

/**
 * What a bare string MEANS in a field that names a language.
 *
 * `inlineRun` already takes a string anywhere a run is expected, and stores it
 * with no language — which is right nearly everywhere, because an absent
 * language means the book's own. Two fields are not nearly everywhere: `he` and
 * `en` say in their names which language they hold, so a string there stored as
 * the book's own would set a Hebrew verse in the Latin face of an English-led
 * book, silently and on every page.
 *
 * So this runs first and states what the field already says. It is not a guess
 * from the characters — that is the thing the model exists to refuse. It is a
 * field declaring its language, read.
 */
const LANG_BY_FIELD: Record<string, Lang> = { he: 'he', en: 'en' }

/**
 * The same content with every string run in a language-naming field expanded,
 * and everything else left for `inlineRun` to expand or refuse.
 *
 * Written from `runsOf`'s PATHS rather than as a switch over the kinds, for the
 * reason `withRunAt` is: a switch here would be a second statement of which
 * runs each kind holds, and the day a kind gains one it would gain it in one
 * place and not the other. A kind that gains a run gains this for nothing.
 *
 * It runs BEFORE validation, so what arrives is unknown and may be anything at
 * all. Anything this cannot recognize is returned exactly as it came, for the
 * schema to refuse in its own words — an expansion that threw here would
 * replace a precise complaint about the payload with a stack trace about
 * reading it.
 */
export function expandRuns(content: unknown): unknown {
  if (!content || typeof content !== 'object' || Array.isArray(content)) {
    return content
  }
  const kind = (content as { kind?: unknown }).kind
  if (typeof kind !== 'string') return content
  try {
    /* Cast because nothing has validated this yet: `runsOf` and `withRunAt` are
       structural walks that read the fields a kind declares, and this payload
       claims a kind. A field holding something other than a run is left alone
       below, and a shape neither walk can read is caught. */
    let next = content as NodeContent
    for (const { path } of runsOf(next)) {
      const field = path.split('.').pop() as string
      const lang = LANG_BY_FIELD[field]
      const run = readAt(next, path)
      if (!lang || typeof run !== 'string') continue
      next = withRunAt(next, path, runOfString(run, lang))
    }
    return next
  } catch {
    /* A payload shaped unlike a node at all — `items` a string where the model
       holds a list of them. Nothing to expand, and the schema says why. */
    return content
  }
}

/** What sits at one of `runsOf`'s paths, before anything has validated it. */
function readAt(content: unknown, path: string): unknown {
  let at: unknown = content
  for (const key of path.split('.')) {
    if (at === null || typeof at !== 'object') return undefined
    at = (at as Record<string, unknown>)[key]
  }
  return at
}

/**
 * A block as a caller may write it.
 *
 * The output is `BlockContent` and nothing else: every shortcut expands on the
 * way in, so the service, the column and the renderer go on seeing one shape.
 */
export const blockInput = z.preprocess(expandRuns, blockContentSchema)

/** The same, over everything a node can be — a heading's title included. */
export const nodeInput = z.preprocess(expandRuns, nodeContentSchema)
